import { FC, Fragment, useMemo, useState } from 'react';
import classNames from 'classnames';
import { differenceInMilliseconds, parseISO, subMonths } from 'date-fns';
import { Else, If, Then } from 'react-if';
import { CreditScoreHistoryIntervalEnum } from '@generated-gql/generated-types';

import { Box } from '@nerdwallet/currency/components/Box';
import { gql } from '@nerdwallet/apollo';
import { Text } from '@nerdwallet/currency/components/Text';
import { getLogger } from '@lib/logging';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';

import { getTierFromScore } from '@components/credit-meter/utils';
import HoverableBlock from '@components/hoverable-block/hoverable-block';
import { useAnalytics } from '@lib/segment';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';

import styles from './credit-history.module.scss';
import { CREDIT_SCORE_MAX, CREDIT_SCORE_MIN } from '@lib/utils/credit-score';
import DashboardChart from '../../credit-score-activated/components/dashboard-chart/dashboard-chart';

const TIME_INTERVALS = {
  ONE_MONTH: { label: '1M', monthsBack: 1 },
  THREE_MONTHS: { label: '3M', monthsBack: 3 },
  SIX_MONTHS: { label: '6M', monthsBack: 6 },
  ONE_YEAR: { label: '1Y', monthsBack: 12 },
  TWO_YEARS: { label: '2Y', monthsBack: 24 },
  THREE_YEARS: { label: '3Y', monthsBack: 36 },
};

export const CREDIT_SCORE_HISTORY_QUERY = gql`
  query CreditScoreHistoryQuery($input: CreditScoreHistoryInput!) {
    creditScoreHistory(input: $input) {
      data {
        vantageScore3
        date
      }
    }
  }
`;
const logger = getLogger('CreditHistory');

interface CreditHistoryProps {
  startDate: string;
  endDate: string;
}

export const CreditHistory: FC<CreditHistoryProps> = ({
  startDate,
  endDate,
}) => {
  const { segmentTrack } = useAnalytics();
  const [selectedTimeInterval, setSelectedTimeInterval] = useState(
    TIME_INTERVALS.ONE_MONTH,
  );

  const { data: creditHistoryData, loading: creditHistoryLoading } =
    useQueryWithLog(logger, CREDIT_SCORE_HISTORY_QUERY, {
      variables: {
        input: {
          interval: CreditScoreHistoryIntervalEnum.All,
          startDate,
          endDate,
        },
      },
    });

  const creditScoreHistory = useMemo(
    () => creditHistoryData?.creditScoreHistory?.data ?? [],
    [creditHistoryData],
  );

  const chartData = useMemo(() => {
    const { monthsBack } = selectedTimeInterval;
    const pastDateLimit = subMonths(new Date(), monthsBack);
    const slice = [];
    // iterate through creditScoreHistory backwards (sorted by chrono order)
    // and grab all values between our date limit and today

    if (creditScoreHistory?.length) {
      // eslint-disable-next-line no-plusplus
      for (let i = creditScoreHistory.length - 1; i >= 0; i--) {
        const { date: pulledAt, vantageScore3: value } = creditScoreHistory[i];
        if (differenceInMilliseconds(pastDateLimit, new Date(pulledAt)) < 0) {
          slice.push({ x: new Date(pulledAt), y: value });
        }
      }
    }

    return slice;
  }, [selectedTimeInterval, creditScoreHistory]);

  const availableTimeIntervals = useMemo(() => {
    if (!creditScoreHistory?.length || creditScoreHistory.length === 0) {
      return [];
    }

    const { date: pulledAt } = creditScoreHistory[0];
    const earliestHistoryDate = parseISO(pulledAt);

    // for best UX, get all intervals fully covered by the user's credit history
    // as well as one interval more (if applicable) to cover the rest of the user's credit history
    let hasHitIntervalLimit = false;
    const filteredIntervals = Object.values(TIME_INTERVALS).filter(
      ({ monthsBack }) => {
        if (hasHitIntervalLimit) return false;
        if (
          differenceInMilliseconds(
            subMonths(new Date(), monthsBack),
            earliestHistoryDate,
          ) < 0
        ) {
          hasHitIntervalLimit = true;
        }
        return true;
      },
    );

    // if there's no available intervals or only one, then we don't need to show any
    return filteredIntervals.length < 2 ? [] : filteredIntervals;
  }, [creditScoreHistory]);

  const { monthsBack, label: selectedIntervalLabel } = selectedTimeInterval;

  const todayAsDateObject = new Date();

  return (
    <Fragment>
      <If condition={creditHistoryLoading}>
        <Then>
          <LoadingSpinner />
        </Then>
      </If>
      <If condition={chartData.length > 0}>
        <Then>
          <div className={styles.dashboardChartContainer}>
            <DashboardChart
              data={chartData}
              xAxisTicks={[
                subMonths(todayAsDateObject, monthsBack),
                todayAsDateObject,
              ]}
              yAxisTicks={[
                CREDIT_SCORE_MIN,
                (CREDIT_SCORE_MIN + CREDIT_SCORE_MAX) / 2,
                CREDIT_SCORE_MAX,
              ]}
              formatFlyoutSubtext={(value) => getTierFromScore(value)}
            />
          </div>
        </Then>
        <Else>
          <Box className={styles.noHistoryContainer}>
            <Text>
              Nothing to see here yet — we just started tracking your score.
              Check again in a few weeks.
            </Text>
          </Box>
        </Else>
      </If>

      <div className={styles.intervalWrapper}>
        <div className={styles.intervalContainer}>
          {creditScoreHistory?.length > 0 &&
            availableTimeIntervals.map((intervalData) => (
              <Box key={intervalData.label} marginX={{ mobile: 1, desktop: 3 }}>
                <HoverableBlock
                  wide={false}
                  onClick={() => {
                    setSelectedTimeInterval(intervalData);

                    segmentTrack('Credit Score History Filter Toggled', {
                      meaningful_engagement_category: 'Cash Score',
                      meaningful_engagement_subcategory: 'Credit Score Summary',
                      meaningful_engagement_flag: true,
                      time_range_selected: intervalData.label,
                    });
                  }}
                >
                  <Box
                    className={classNames(styles.intervalLink, {
                      [styles.intervalLinkActive]:
                        intervalData.label === selectedIntervalLabel,
                    })}
                  >
                    <Text>{intervalData.label}</Text>
                  </Box>
                </HoverableBlock>
              </Box>
            ))}
        </div>
      </div>
    </Fragment>
  );
};
